// Security headers, CSP, and security policies
import { env } from "../config/environment";

export interface SecurityConfig {
  enableCSP: boolean;
  enableHSTS: boolean;
  enableXSSProtection: boolean;
  enableContentTypeOptions: boolean;
  enableFrameOptions: boolean;
  enableReferrerPolicy: boolean;
  enablePermissionsPolicy: boolean;
}

export const DEFAULT_SECURITY_CONFIG: SecurityConfig = {
  enableCSP: true,
  enableHSTS: true,
  enableXSSProtection: true,
  enableContentTypeOptions: true,
  enableFrameOptions: true,
  enableReferrerPolicy: true,
  enablePermissionsPolicy: true,
};

export class SecurityMiddleware {
  private config: SecurityConfig;

  constructor(config: Partial<SecurityConfig> = {}) {
    this.config = { ...DEFAULT_SECURITY_CONFIG, ...config };
  }

  // Generate Content Security Policy
  private generateCSP(): string {
    const policies = [
      // Default source restrictions
      "default-src 'self'",
      // Script sources - allow inline scripts for development, restrict in production
      import.meta.env.PROD ? "script-src 'self' 'unsafe-inline' 'unsafe-eval'" : "script-src 'self' 'unsafe-inline' 'unsafe-eval'",
      // Style sources
      "style-src 'self' 'unsafe-inline' https://fonts.googleapis.com",
      // Font sources
      "font-src 'self' https://fonts.gstatic.com data:",
      // Image sources
      "img-src 'self' data: https: blob:",
      // Connect sources for API calls
      "connect-src 'self' ws: wss: https:",
      // Media sources
      "media-src 'self'",
      // Object sources
      "object-src 'none'",
      // Base URI
      "base-uri 'self'",
      // Form actions
      "form-action 'self'",
      // Frame ancestors (prevent clickjacking)
      "frame-ancestors 'none'",
      // Upgrade insecure requests
      "upgrade-insecure-requests",
    ];

    return policies.join("; ");
  }

  // Generate security headers
  public getSecurityHeaders(): Record<string, string> {
    const headers: Record<string, string> = {};

    // Content Security Policy
    if (this.config.enableCSP) {
      headers["Content-Security-Policy"] = this.generateCSP();
    }

    // HTTP Strict Transport Security
    if (this.config.enableHSTS) {
      headers["Strict-Transport-Security"] = "max-age=31536000; includeSubDomains; preload";
    }

    // X-Content-Type-Options
    if (this.config.enableContentTypeOptions) {
      headers["X-Content-Type-Options"] = "nosniff";
    }

    // X-Frame-Options
    if (this.config.enableFrameOptions) {
      headers["X-Frame-Options"] = "DENY";
    }

    // X-XSS-Protection
    if (this.config.enableXSSProtection) {
      headers["X-XSS-Protection"] = "1; mode=block";
    }

    // Referrer Policy
    if (this.config.enableReferrerPolicy) {
      headers["Referrer-Policy"] = "strict-origin-when-cross-origin";
    }

    // Permissions Policy
    if (this.config.enablePermissionsPolicy) {
      headers["Permissions-Policy"] = ["camera=()", "microphone=()", "geolocation=()", "payment=()", "usb=()", "magnetometer=()", "gyroscope=()", "accelerometer=()"].join(", ");
    }

    // Additional security headers
    headers["X-DNS-Prefetch-Control"] = "off";
    headers["X-Download-Options"] = "noopen";
    headers["X-Permitted-Cross-Domain-Policies"] = "none";

    return headers;
  }

  // Validate request origin
  public validateOrigin(origin: string): boolean {
    const allowedOrigins = [env.FRONTEND_URL, env.API_BASE_URL].filter(Boolean);
    return allowedOrigins.includes(origin);
  }

  // Sanitize user input
  public sanitizeInput(input: string): string {
    return input
      .replace(/[<>]/g, "") // Remove < and >
      .replace(/javascript:/gi, "") // Remove javascript: protocol
      .replace(/on\w+=/gi, "") // Remove event handlers
      .trim();
  }

  // Validate API key format
  public validateAPIKey(apiKey: string): boolean {
    // Basic API key validation (adjust based on your requirements)
    const apiKeyRegex = /^[a-zA-Z0-9]{32,64}$/;
    return apiKeyRegex.test(apiKey);
  }

  // Rate limiting helper
  public createRateLimiter(maxRequests: number = 100, windowMs: number = 60000) {
    const requests = new Map<string, { count: number; resetTime: number }>();

    return (identifier: string): boolean => {
      const now = Date.now();
      const userRequests = requests.get(identifier);

      if (!userRequests || now > userRequests.resetTime) {
        requests.set(identifier, { count: 1, resetTime: now + windowMs });
        return true;
      }

      if (userRequests.count >= maxRequests) {
        return false;
      }

      userRequests.count++;
      return true;
    };
  }

  // CSRF token validation
  public validateCSRFToken(token: string, sessionToken: string): boolean {
    // In a real implementation, you'd validate against stored session tokens
    return token === sessionToken && token.length >= 32;
  }

  // Generate secure random token
  public generateSecureToken(length: number = 32): string {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let result = "";
    for (let i = 0; i < length; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  }
}

// Export singleton instance
export const securityMiddleware = new SecurityMiddleware();

// Export security headers for use in development server
export const getSecurityHeaders = () => securityMiddleware.getSecurityHeaders();
